import { useState, useEffect } from 'react';
import { Plus, Upload, Search, Package, ChevronLeft, ChevronRight, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useFetchProductCatalog } from '@/hooksApi/useProductCatalog';
import { useProductCatalogStore } from '@/stores/productCatalogStore';
import { SettingsBreadcrumb } from '@/components/SettingsBreadcrumb';
import { SettingsListLoading } from '@/components/SettingsListLoading';
import { ProductRow } from './ProductRow';
import { CsvImportModal } from './CsvImportModal';

const PAGE_SIZE = 20;

const SettingsProductCatalog = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Store state
  const searchQuery = useProductCatalogStore((s) => s.searchQuery);
  const setSearchQuery = useProductCatalogStore((s) => s.setSearchQuery);
  const currentPage = useProductCatalogStore((s) => s.currentPage);
  const setCurrentPage = useProductCatalogStore((s) => s.setCurrentPage);

  // Local search input with 300ms debounce
  const [localSearch, setLocalSearch] = useState(searchQuery);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchQuery(localSearch);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [localSearch, setSearchQuery, setCurrentPage]);

  // CSV import modal
  const [showImport, setShowImport] = useState(false);

  // Data
  const { data, isLoading } = useFetchProductCatalog(
    currentPage,
    PAGE_SIZE,
    searchQuery || undefined,
  );
  const products = data?.object?.items ?? [];
  const totalPages = data?.object?.pages ?? 0;
  const totalItems = data?.object?.total ?? 0;

  const hasPrev = currentPage > 1;
  const hasNext = currentPage < totalPages;

  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className='sd-scope sd-font flex flex-col'>
      <div className='p-6 space-y-6 max-w-3xl w-full mx-auto'>
        <SettingsBreadcrumb
          moduleName='product_catalog'
          moduleRoute='/settings/product-catalog'
        />

        {/* Header */}
        <div className='flex items-center justify-between'>
          <h1 className='text-xl font-semibold text-sd-foreground m-0'>
            {String(t('product_catalog', { defaultValue: 'Product Catalog' }))}
          </h1>
          <div className='flex items-center gap-2'>
            <button
              onClick={() => setShowImport(true)}
              className='flex items-center gap-2 px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
            >
              <Upload size={16} />
              {String(t('import_from_csv', { defaultValue: 'Import CSV' }))}
            </button>
            <button
              onClick={() => navigate('/settings/product-catalog/new')}
              className='flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
            >
              <Plus className='h-4 w-4' />
              {String(t('add_product', { defaultValue: 'Add Product' }))}
            </button>
          </div>
        </div>

        {/* Search bar */}
        <div className='relative'>
          <Search className='absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-sd-muted-foreground pointer-events-none' />
          <input
            type='text'
            value={localSearch}
            onChange={(e) => setLocalSearch(e.target.value)}
            placeholder={String(t('search_products', { defaultValue: 'Search products...' }))}
            aria-label={String(t('search_products', { defaultValue: 'Search products' }))}
            className='w-full h-10 ps-9 pe-8 rounded-lg border border-sd-input bg-sd-input-bg text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring'
          />
          {localSearch && (
            <button
              onClick={() => setLocalSearch('')}
              aria-label={String(t('clear_search', { defaultValue: 'Clear search' }))}
              className='absolute end-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded text-sd-muted-foreground hover:text-sd-foreground transition-colors'
            >
              <X className='h-3.5 w-3.5' />
            </button>
          )}
        </div>

        {/* Content */}
        {isLoading ? (
          <SettingsListLoading />
        ) : products.length === 0 ? (
          <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm p-10 flex flex-col items-center text-center gap-3'>
            <div className='h-12 w-12 rounded-xl bg-sd-muted flex items-center justify-center'>
              <Package className='h-6 w-6 text-sd-muted-foreground' />
            </div>
            <div className='space-y-1'>
              <p className='text-sm font-medium text-sd-foreground'>
                {searchQuery
                  ? String(t('no_products_match', { query: searchQuery, defaultValue: `No products match "${searchQuery}"` }))
                  : String(t('no_products_yet', { defaultValue: 'No products yet' }))}
              </p>
              <p className='text-xs text-sd-muted-foreground'>
                {searchQuery
                  ? String(t('no_products_match_description', { defaultValue: 'Try a different search term.' }))
                  : String(t('no_products_description', { defaultValue: 'Add your first product to get started.' }))}
              </p>
            </div>
            {searchQuery ? (
              <button
                onClick={() => setLocalSearch('')}
                className='mt-1 px-4 py-2 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors'
              >
                {String(t('clear_search', { defaultValue: 'Clear search' }))}
              </button>
            ) : (
              <button
                onClick={() => navigate('/settings/product-catalog/new')}
                className='mt-1 flex items-center gap-2 px-4 py-2 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity'
              >
                <Plus className='h-4 w-4' />
                {String(t('add_product', { defaultValue: 'Add Product' }))}
              </button>
            )}
          </div>
        ) : (
          <div className='space-y-3'>
            {products.map((product) => (
              <ProductRow
                key={product.uid}
                product={product}
                onEdit={(uid) => navigate(`/settings/product-catalog/${uid}/edit`)}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='flex items-center justify-center gap-4'>
            <button
              disabled={!hasPrev}
              onClick={() => setCurrentPage(currentPage - 1)}
              className='flex items-center gap-1 px-3 py-1.5 rounded-lg border border-sd-input bg-sd-background text-xs font-medium hover:bg-sd-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              <ChevronLeft size={14} />
              {String(t('previous', { defaultValue: 'Previous' }))}
            </button>
            <span className='text-xs text-sd-muted-foreground tabular-nums'>
              {totalItems > 0
                ? `${startItem}–${endItem} / ${totalItems}`
                : `${currentPage} / ${totalPages}`}
            </span>
            <button
              disabled={!hasNext}
              onClick={() => setCurrentPage(currentPage + 1)}
              className='flex items-center gap-1 px-3 py-1.5 rounded-lg border border-sd-input bg-sd-background text-xs font-medium hover:bg-sd-muted transition-colors disabled:opacity-50 disabled:cursor-not-allowed'
            >
              {String(t('next', { defaultValue: 'Next' }))}
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* CSV Import Modal */}
      <CsvImportModal open={showImport} onClose={() => setShowImport(false)} />
    </div>
  );
};

export default SettingsProductCatalog;
